import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Header } from '../components/Header';
import { ScoreTracker } from '../components/ScoreTracker';
import { StudyCard } from '../components/FlashcardFlip';
import { Button } from '../components/ui/button';
import { ArrowLeft, ArrowRight, RotateCcw, Check, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function shuffleArray(array) {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
}

function getDistractors(allCards, correctCard, count = 3) {
    const others = allCards.filter(c => c.id !== correctCard.id);
    const shuffled = shuffleArray(others);
    return shuffled.slice(0, count);
}

const CHOICE_KEYS = ['A', 'B', 'C', 'D'];

export default function MultipleChoicePage() {
    const { setId } = useParams();
    const navigate = useNavigate();

    const [flashcardSet, setFlashcardSet] = useState(null);
    const [cards, setCards] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [choices, setChoices] = useState([]);
    const [selected, setSelected] = useState(null);
    const [loading, setLoading] = useState(true);

    const [score, setScore] = useState({ correct: 0, incorrect: 0 });
    const [completed, setCompleted] = useState(false);

    useEffect(() => {
        fetchSet();
    }, [setId]);

    useEffect(() => {
        if (cards.length > 0 && currentIndex < cards.length) {
            generateChoices();
        }
    }, [currentIndex, cards]);

    const fetchSet = async () => {
        try {
            const response = await axios.get(`${API}/sets/${setId}`);
            setFlashcardSet(response.data);
            setCards(shuffleArray(response.data.flashcards));
        } catch (err) {
            console.error('Failed to load set:', err);
        } finally {
            setLoading(false);
        }
    };

    const generateChoices = () => {
        const currentCard = cards[currentIndex];
        const distractors = getDistractors(cards, currentCard);
        const allChoices = [currentCard, ...distractors];
        setChoices(shuffleArray(allChoices));
        setSelected(null);
    };

    const currentCard = cards[currentIndex];

    const handleSelect = useCallback((choice) => {
        if (selected) return;
        
        setSelected(choice);
        
        if (choice.id === currentCard.id) {
            setScore(prev => ({ ...prev, correct: prev.correct + 1 }));
        } else {
            setScore(prev => ({ ...prev, incorrect: prev.incorrect + 1 }));
        }
    }, [selected, currentCard]);

    const nextCard = () => {
        if (currentIndex >= cards.length - 1) {
            setCompleted(true);
        } else {
            setCurrentIndex(currentIndex + 1);
        }
    };

    const restart = () => {
        setCards(shuffleArray(flashcardSet.flashcards));
        setCurrentIndex(0);
        setSelected(null);
        setScore({ correct: 0, incorrect: 0 });
        setCompleted(false);
    };

    // Keyboard support
    useEffect(() => {
        const handleKeyDown = (e) => {
            const key = e.key.toUpperCase();
            const index = CHOICE_KEYS.indexOf(key);
            
            if (index !== -1 && index < choices.length && !selected) {
                handleSelect(choices[index]);
            } else if ((e.key === 'Enter' || e.key === ' ') && selected) {
                e.preventDefault();
                nextCard();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [choices, selected, handleSelect]);

    if (loading) {
        return (
            <div className="min-h-screen bg-background">
                <Header />
                <div className="pt-24 flex items-center justify-center min-h-[80vh]">
                    <div className="animate-pulse text-muted-foreground">Loading...</div>
                </div>
            </div>
        );
    }

    if (completed) {
        const totalAnswered = score.correct + score.incorrect;
        const percentage = Math.round((score.correct / totalAnswered) * 100);

        return (
            <div className="min-h-screen bg-background">
                <Header />
                <main className="pt-24 pb-16 px-6">
                    <div className="max-w-2xl mx-auto study-centered">
                        <div 
                            data-testid="completion-screen"
                            className="text-center"
                        >
                            <h1 className="font-serif text-4xl tracking-tight mb-4">
                                Session Complete!
                            </h1>
                            <p className="font-mono text-6xl text-primary mb-8">
                                {percentage}%
                            </p>

                            <div className="flex justify-center gap-8 mb-12">
                                <div className="text-center">
                                    <p className="font-mono text-2xl text-green-600">{score.correct}</p>
                                    <p className="text-sm text-muted-foreground">Correct</p>
                                </div>
                                <div className="text-center">
                                    <p className="font-mono text-2xl text-red-600">{score.incorrect}</p>
                                    <p className="text-sm text-muted-foreground">Incorrect</p>
                                </div>
                            </div>

                            <div className="flex justify-center gap-4">
                                <Button
                                    variant="outline"
                                    onClick={() => navigate(`/study/${setId}`)}
                                    data-testid="back-to-modes-button"
                                    className="rounded-none"
                                >
                                    <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
                                    Study Modes
                                </Button>
                                <Button
                                    onClick={restart}
                                    data-testid="restart-button"
                                    className="rounded-none"
                                >
                                    <RotateCcw className="w-4 h-4 mr-2" strokeWidth={1.5} />
                                    Try Again
                                </Button>
                            </div>
                        </div>
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background">
            <Header />
            <ScoreTracker 
                correct={score.correct}
                incorrect={score.incorrect}
                total={cards.length}
                current={currentIndex + 1}
            />

            <main className="pt-8 pb-16 px-6">
                <div className="max-w-2xl mx-auto">
                    {/* Back Button */}
                    <Button
                        variant="ghost"
                        onClick={() => navigate(`/study/${setId}`)}
                        data-testid="back-button"
                        className="rounded-none mb-8"
                    >
                        <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
                        Back
                    </Button>

                    {/* Card Display - Shows Term */}
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={currentIndex}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            className="mb-8"
                        >
                            <StudyCard>
                                <span className="font-mono text-xs tracking-widest uppercase text-muted-foreground mb-4">
                                    TERM
                                </span>
                                <p 
                                    data-testid="card-display-text"
                                    className="font-serif text-2xl md:text-3xl text-center tracking-tight"
                                >
                                    {currentCard?.term}
                                </p>
                            </StudyCard>
                        </motion.div>
                    </AnimatePresence>

                    {/* Multiple Choice Options */}
                    <div className="space-y-4">
                        <label className="font-mono text-xs tracking-wider uppercase text-muted-foreground">
                            Select the correct definition
                        </label>

                        <div className="choice-grid">
                            {choices.map((choice, index) => {
                                const isCorrect = choice.id === currentCard?.id;
                                const isSelected = selected?.id === choice.id;
                                const showResult = selected !== null;

                                let buttonClass = 'border-border hover:border-primary hover:bg-primary/5';
                                if (showResult) {
                                    if (isCorrect) {
                                        buttonClass = 'border-green-500 bg-green-500/10';
                                    } else if (isSelected && !isCorrect) {
                                        buttonClass = 'border-red-500 bg-red-500/10';
                                    } else {
                                        buttonClass = 'border-border opacity-50';
                                    }
                                }

                                return (
                                    <motion.button
                                        key={choice.id}
                                        onClick={() => handleSelect(choice)}
                                        disabled={selected !== null}
                                        data-testid={`choice-${index}`}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: index * 0.05 }}
                                        className={`relative p-4 border-2 text-left transition-colors ${buttonClass} ${!showResult ? 'btn-lift cursor-pointer' : ''}`}
                                    >
                                        <span className="absolute top-2 left-2 font-mono text-xs text-muted-foreground">
                                            {CHOICE_KEYS[index]}
                                        </span>
                                        
                                        <div className="pt-4">
                                            <p className="font-sans text-sm leading-relaxed">
                                                {choice.definition}
                                            </p>
                                        </div>

                                        {showResult && isCorrect && (
                                            <Check className="absolute top-2 right-2 w-4 h-4 text-green-600" strokeWidth={2} />
                                        )}
                                        {showResult && isSelected && !isCorrect && (
                                            <X className="absolute top-2 right-2 w-4 h-4 text-red-600" strokeWidth={2} />
                                        )}
                                    </motion.button>
                                );
                            })}
                        </div>

                        {/* Next Button */}
                        {selected && (
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                            >
                                <Button
                                    onClick={nextCard}
                                    data-testid="next-card-button"
                                    className="w-full rounded-none h-12 mt-4"
                                >
                                    {currentIndex >= cards.length - 1 ? 'See Results' : 'Next Card'}
                                    <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
                                </Button>
                            </motion.div>
                        )}

                        {/* Keyboard hint */}
                        <p className="text-center text-xs text-muted-foreground mt-4">
                            Press A, B, C, D to select • Enter to continue
                        </p>
                    </div>
                </div>
            </main>
        </div>
    );
}
